// Router
// URL-based view routing logic

import { getQueueDocument } from '../services/firestore-service.js';
import { functions, httpsCallable } from '../config/firebase-config.js';
import { STATUS } from '../config/constants.js';
import { isStaffUser } from './auth-manager.js';
import stateManager from './state-manager.js';

/**
 * Validate token against config via Cloud Function (server-side)
 * El token se valida en el servidor para no exponer config/secrets al cliente
 * @param {string} token - Token to validate
 * @returns {Promise<boolean>} Whether token is valid
 */
export async function validateToken(token) {
    if (!token) return false;

    try {
        const validateQRToken = httpsCallable(functions, 'validateQRToken');
        const result = await validateQRToken({ token });
        return result.data?.valid === true;
    } catch (error) {
        console.error("Error validating token:", error);
        return false;
    }
}

/**
 * Initial view router
 * Determines which view to show based on URL parameters and state
 * @param {function} showView - Function to show a specific view
 * @param {function} startQueueListeners - Function to start Firestore listeners
 * @returns {Promise<string>} The view that was routed to
 */
export async function initialViewRouter(showView, startQueueListeners) {
    const urlParams = new URLSearchParams(window.location.search);
    const currentUser = stateManager.getState('currentUser');

    // Handle WhatsApp link with ?id= parameter
    if (urlParams.has('id')) {
        const customerIdFromUrl = urlParams.get('id');
        console.log('🔍 DEBUG: Accessing with ID:', customerIdFromUrl);

        const customerDoc = await getQueueDocument(customerIdFromUrl);
        console.log('🔍 DEBUG: Document exists?', !!customerDoc);

        if (customerDoc) {
            console.log('🔍 DEBUG: Customer status:', customerDoc.status);

            // Only allow access if customer is in active status
            const activeStatuses = [STATUS.WAITING, STATUS.STANDBY, STATUS.CALLING, STATUS.SERVING];
            if (activeStatuses.includes(customerDoc.status)) {
                stateManager.setCurrentCustomerId(customerIdFromUrl);
                showView('waiting');
                startQueueListeners();
                return 'waiting';
            } else {
                console.log('❌ DEBUG: Customer not in active status. Current:', customerDoc.status);
            }
        } else {
            console.log('❌ DEBUG: Customer does not exist in Firestore');
        }

        // Customer doesn't exist or already attended
        showView('tokenError');
        return 'tokenError';
    }

    // Handle staff view (must check before other logic)
    if (urlParams.get('view') === 'staff') {
        if (isStaffUser(currentUser)) {
            showView('staff');
            startQueueListeners();
        } else {
            showView('login');
        }
        return urlParams.get('view');
    }

    // Handle staff view v2 (new design)
    if (urlParams.get('view') === 'staff-v2') {
        if (isStaffUser(currentUser)) {
            showView('staff-v2');
            startQueueListeners();
        } else {
            showView('login');
        }
        return urlParams.get('view');
    }

    // Handle host view
    if (urlParams.get('view') === 'host') {
        if (isStaffUser(currentUser)) {
            showView('host');
            startQueueListeners();
        } else {
            showView('login');
        }
        return urlParams.get('view');
    }

    // Handle survey view
    if (urlParams.has('survey_id')) {
        const surveyId = urlParams.get('survey_id');
        stateManager.setCurrentCustomerId(surveyId);
        showView('survey');
        return 'survey';
    }

    // Check localStorage for existing customer
    const customerIdFromStorage = stateManager.getState('currentCustomerId');

    if (customerIdFromStorage) {
        const customerDoc = await getQueueDocument(customerIdFromStorage);

        if (customerDoc && customerDoc.status !== STATUS.DONE) {
            showView('waiting');
            startQueueListeners();
            return 'waiting';
        } else {
            // Customer is done or doesn't exist
            stateManager.clearCurrentCustomerId();
            return await showViewBasedOnToken(urlParams, showView);
        }
    } else {
        // No customer in localStorage
        return await showViewBasedOnToken(urlParams, showView);
    }
}

/**
 * Show view based on token validation
 * @param {URLSearchParams} urlParams - URL parameters
 * @param {function} showView - Function to show a specific view
 * @returns {Promise<string>} The view that was shown
 */
async function showViewBasedOnToken(urlParams, showView) {
    const isTokenValid = await validateToken(urlParams.get('token'));

    if (isTokenValid) {
        showView('register');
        return 'register';
    } else {
        showView('tokenError');
        return 'tokenError';
    }
}

/**
 * Navigate to survey view
 * @param {string} customerId - Customer ID
 */
export function showSurveyViewWithId(customerId) {
    const url = new URL(window.location.href);
    url.searchParams.set('survey_id', customerId);
    window.location.href = url.toString();
}
